<template>
  <div class="admin-layout">
    <!-- Environment Banner (PRODUCTION warning) -->
    <div v-if="isProduction" class="env-banner production">
      <span>⚠️ PRODUCTION ENVIRONMENT</span>
    </div>
    <div v-else class="env-banner development">
      <span>🔧 DEVELOPMENT</span>
    </div>

    <!-- Preview Mode Banner -->
    <div v-if="auth.isPreviewMode" class="env-banner preview-mode">
      <span>🔍 PREVIEW MODE: {{ previewModeLabel }}</span>
      <button class="preview-exit-btn" @click="auth.clearPreview()">
        Exit Preview
      </button>
    </div>

    <div class="admin-container">
      <!-- Left Sidebar Navigation -->
      <aside class="admin-sidebar">
        <div class="sidebar-header">
          <img
            src="/assets/nilassist-logo.svg"
            alt="NILAssist"
            class="admin-logo"
          >
          <!-- Role Badge with Data Scope -->
          <div class="role-badge" :class="roleBadgeClass">
            <span class="role-name">{{ userRole }}</span>
            <span v-if="dataScope" class="data-scope">{{ dataScope }}</span>
          </div>
          <!-- PII Access Indicator -->
          <div v-if="piiAccess" class="pii-access" :class="piiAccessClass">
            {{ piiAccessLabel }}
          </div>
        </div>

        <nav class="sidebar-nav">
          <!-- Dashboard -->
          <router-link to="/admin" class="nav-item" exact-active-class="active">
            <span class="nav-icon">📊</span>
            <span class="nav-label">Dashboard</span>
          </router-link>

          <!-- Users (SUPERADMIN, CUSTOMER_SERVICE only) -->
          <template v-if="canAccessUsers">
            <div class="nav-section-title">
              Users
            </div>
            <router-link to="/admin/users" class="nav-item" active-class="active">
              <span class="nav-icon">👥</span>
              <span class="nav-label">Athletes</span>
            </router-link>
          </template>

          <!-- Admin Staff (SUPERADMIN only) -->
          <template v-if="isEffectiveSuperadmin">
            <div class="nav-section-title">
              Administration
            </div>
            <router-link to="/admin/staff" class="nav-item" active-class="active">
              <span class="nav-icon">👨‍💼</span>
              <span class="nav-label">Admin Users</span>
            </router-link>
            <router-link to="/admin/epicai-docs" class="nav-item" active-class="active">
              <span class="nav-icon">📚</span>
              <span class="nav-label">Epic AI Docs</span>
            </router-link>
          </template>

          <!-- Commerce (All roles except DEAL_CUSTOMER) -->
          <template v-if="canAccessCommerce">
            <div class="nav-section-title">
              Commerce
            </div>
            <router-link to="/admin/deals" class="nav-item" active-class="active">
              <span class="nav-icon">💰</span>
              <span class="nav-label">{{ dealsLabel }}</span>
            </router-link>
            <router-link to="/admin/transactions" class="nav-item" active-class="active">
              <span class="nav-icon">💳</span>
              <span class="nav-label">{{ transactionsLabel }}</span>
            </router-link>
          </template>

          <!-- Self-Service (DEAL_SPONSOR only) -->
          <template v-if="isDealSponsor">
            <div class="nav-section-title">
              My Account
            </div>
            <router-link to="/admin/profile" class="nav-item" active-class="active">
              <span class="nav-icon">👤</span>
              <span class="nav-label">My Profile</span>
            </router-link>
            <router-link to="/admin/my-deals" class="nav-item" active-class="active">
              <span class="nav-icon">💰</span>
              <span class="nav-label">My Deals</span>
            </router-link>
            <router-link to="/admin/my-transactions" class="nav-item" active-class="active">
              <span class="nav-icon">💳</span>
              <span class="nav-label">My Transactions</span>
            </router-link>
          </template>

          <!-- School Compliance (COMPLIANCE role only) -->
          <template v-if="canAccessCompliance">
            <div class="nav-section-title">
              School Compliance
            </div>
            <router-link to="/admin/compliance/dashboard" class="nav-item" active-class="active">
              <span class="nav-icon">📊</span>
              <span class="nav-label">Compliance Dashboard</span>
            </router-link>
            <router-link to="/admin/compliance/athletes" class="nav-item" active-class="active">
              <span class="nav-icon">👥</span>
              <span class="nav-label">School Athletes</span>
            </router-link>
            <router-link to="/admin/compliance/deals" class="nav-item" active-class="active">
              <span class="nav-icon">💰</span>
              <span class="nav-label">Athlete Deals</span>
            </router-link>
            <router-link to="/admin/compliance/reports" class="nav-item" active-class="active">
              <span class="nav-icon">📋</span>
              <span class="nav-label">Compliance Reports</span>
            </router-link>
          </template>

          <!-- System (SUPERADMIN only - hides in preview mode) -->
          <template v-if="isEffectiveSuperadmin">
            <div class="nav-section-title">
              System
            </div>
            <router-link to="/admin/profile-tools" class="nav-item" active-class="active">
              <span class="nav-icon">⚙️</span>
              <span class="nav-label">Profile/Tools</span>
            </router-link>
            <router-link to="/admin/audit-logs" class="nav-item" active-class="active">
              <span class="nav-icon">📋</span>
              <span class="nav-label">Audit Logs</span>
            </router-link>
            <router-link to="/admin/collections" class="nav-item" active-class="active">
              <span class="nav-icon">🗄️</span>
              <span class="nav-label">Database Browser</span>
            </router-link>
          </template>
        </nav>

        <div class="sidebar-footer">
          <div class="user-info">
            <div class="user-email">
              {{ userEmail }}
            </div>
            <button class="logout-btn" @click="handleLogout">
              Logout
            </button>
          </div>
        </div>
      </aside>

      <!-- Main Content Area -->
      <main class="admin-main">
        <router-view />
      </main>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useAuthStore } from '@/stores/auth';
import { useRouter } from 'vue-router';

const auth = useAuthStore();
const router = useRouter();

// Environment detection
const isProduction = computed(() => import.meta.env.PROD);

// User role and permissions
// Use effectiveRole for preview mode (SUPERADMIN can preview as other roles)
const effectiveRole = computed(() => auth.effectiveRole || 'UNKNOWN');
const actualRole = computed(() => auth.user?.userType || 'UNKNOWN');
const userRole = effectiveRole; // Alias for template compatibility
const userEmail = computed(() => auth.user?.email || 'unknown@example.com');

// Role-based UI (uses effectiveRole so preview mode works)
// isSuperadmin uses actualRole - only for PII/data scope display, not navigation
const isSuperadmin = computed(() => actualRole.value === 'SuperAdmin');
const isEffectiveSuperadmin = computed(() => effectiveRole.value === 'SuperAdmin');
const isCompliance = computed(() => effectiveRole.value === 'ComplianceOfficer');
const isCustomerService = computed(() => effectiveRole.value === 'CustomerService');
const isDealSponsor = computed(() => effectiveRole.value === 'DealSponsor');

// Navigation visibility (uses effective roles for preview mode)
const canAccessUsers = computed(() =>
  isEffectiveSuperadmin.value || isCustomerService.value
);

const canAccessCommerce = computed(() =>
  isEffectiveSuperadmin.value || isCustomerService.value
);

const canAccessCompliance = computed(() =>
  isCompliance.value
);

// Labels change based on role
const dealsLabel = computed(() => {
  if (isDealSponsor.value) return 'My Deals';
  return 'Deals';
});

const transactionsLabel = computed(() => {
  if (isDealSponsor.value) return 'My Transactions';
  return 'Transactions';
});

// Role badge styling
const roleBadgeClass = computed(() => {
  const classes: Record<string, string> = {
    SuperAdmin: 'role-superadmin',
    ComplianceOfficer: 'role-compliance',
    CustomerService: 'role-customer-service',
    DealSponsor: 'role-deal-sponsor',
  };
  return classes[userRole.value] || 'role-unknown';
});

// Data scope label
const dataScope = computed(() => {
  if (isSuperadmin.value || isCustomerService.value) {
    return 'Full Access';
  }
  if (isCompliance.value) {
    const schoolName = auth.user?.schoolName;
    const schoolId = auth.user?.schoolId;
    return schoolName ? `School: ${schoolName}` : schoolId ? `School: ${schoolId}` : 'School Scoped';
  }
  if (isDealSponsor.value) {
    return 'Self Only';
  }
  return null;
});

// PII access level - ACCURATE per role
const piiAccess = computed(() => {
  // Full PII access: SUPERADMIN, COMPLIANCE_OFFICER, CUSTOMER_SERVICE
  if (isSuperadmin.value || isCompliance.value || isCustomerService.value) {
    return 'full';
  }
  // Own data only: DEAL_SPONSOR
  if (isDealSponsor.value) {
    return 'own';
  }
  return null;
});

const piiAccessClass = computed(() => ({
  'pii-full': piiAccess.value === 'full',
  'pii-own': piiAccess.value === 'own',
}));

const piiAccessLabel = computed(() => {
  if (piiAccess.value === 'full') return 'Full PII Access';
  if (piiAccess.value === 'own') return 'Own PII Only';
  return '';
});

// Preview mode label
const previewModeLabel = computed(() => {
  const parts: string[] = [];
  if (auth.previewRole) {
    const roleNames: Record<string, string> = {
      SuperAdmin: 'Super Admin',
      DevOps: 'Operations',
      ComplianceOfficer: 'Compliance',
      Finance: 'Finance',
      CustomerService: 'Customer Service',
      DealSponsor: 'Deal Sponsor'
    };
    parts.push(`Role: ${roleNames[auth.previewRole] || auth.previewRole}`);
  }
  if (auth.previewBranding) {
    parts.push(`Branding: ${auth.previewBranding.name}`);
  }
  return parts.join(' | ');
});

// Logout handler
const handleLogout = async () => {
  await auth.logout();
  router.push('/');
};
</script>

<style scoped>
/* Layout Container - Fullscreen with Liquid Glass */
.admin-layout {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--color-surface-primary);
}

/* Environment Banner - Sticky glass bar at top */
.env-banner {
  padding: var(--space-inset-sm);
  text-align: center;
  font-weight: 600;
  font-size: var(--text-caption);
  position: sticky;
  top: 0;
  z-index: var(--z-banner);
  backdrop-filter: blur(var(--blur-surface-glass-medium));
}

.env-banner.production {
  background: var(--color-feedback-critical);
  color: var(--color-text-on-accent);
}

.env-banner.development {
  background: var(--color-feedback-info);
  color: var(--color-text-on-accent);
}

.env-banner.preview-mode {
  background: var(--color-feedback-warning);
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-inline-md);
}

.preview-exit-btn {
  padding: var(--space-inset-xs) var(--space-inset-sm);
  background: var(--color-surface-glass-strong);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-button);
  font-size: var(--text-label);
  font-weight: 500;
  cursor: pointer;
}

/* Admin Container - Responsive flex with container queries */
.admin-container {
  display: flex;
  flex: 1;
  min-height: 0;
  container-type: inline-size;
  container-name: admin-layout;
}

/* Sidebar - Glass surface with blur, responsive width via clamp */
.admin-sidebar {
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  background: var(--color-surface-glass-strong);
  backdrop-filter: blur(var(--blur-surface-glass-high));
  border-right: 1px solid var(--color-border-subtle);
  color: var(--color-text-primary);
  width: clamp(15rem, 20vw, 20rem);
}

.sidebar-header {
  padding: var(--space-inset-lg);
  border-bottom: 1px solid var(--color-border-subtle);
}

.admin-logo {
  width: 100%;
  max-width: 12rem;
  height: auto;
  margin-bottom: var(--space-stack-md);
  display: block;
}

/* Role Badge - Glass pill with color coding */
.role-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  padding: var(--space-inset-sm) var(--space-inset-md);
  border-radius: var(--radius-pill);
  font-size: var(--text-label);
  font-weight: 600;
  margin-bottom: var(--space-stack-sm);
  backdrop-filter: blur(var(--blur-surface-glass-low));
}

/* All role badges use subtle glass - NO bright colors */
.role-superadmin,
.role-operations,
.role-compliance,
.role-finance,
.role-customer-service,
.role-deal-sponsor {
  background: var(--color-surface-glass-strong);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
}

.role-name {
  font-size: var(--text-body);
  margin-bottom: var(--space-stack-xs);
}

.data-scope {
  font-size: var(--text-label);
  opacity: var(--opacity-muted);
}

/* PII Access Indicator */
.pii-access {
  display: inline-block;
  padding: var(--space-inset-xs) var(--space-inset-sm);
  border-radius: var(--radius-button);
  font-size: var(--text-label);
  font-weight: 500;
  backdrop-filter: blur(var(--blur-surface-glass-low));
}

/* All PII badges use subtle glass - NO bright colors */
.pii-full,
.pii-masked,
.pii-own {
  background: var(--color-surface-glass-strong);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: var(--space-inset-md) 0;
  overflow-y: auto;
}

.nav-section-title {
  padding: var(--space-inset-md) var(--space-inset-lg) var(--space-inset-sm);
  font-size: var(--text-label);
  font-weight: 600;
  text-transform: uppercase;
  color: var(--color-text-muted);
  letter-spacing: 0.05em;
}

.nav-item {
  display: flex;
  align-items: center;
  padding: var(--button-padding-vertical-md) var(--button-padding-inline-lg);
  color: var(--color-text-secondary);
  text-decoration: none;
  transition: all var(--motion-fast-ease-out);
  border-radius: var(--radius-button);
  margin: 0 var(--space-inline-sm);
}

.nav-item:hover {
  background: var(--color-surface-glass);
  color: var(--color-text-primary);
  backdrop-filter: blur(var(--blur-surface-glass-medium));
}

.nav-item.active {
  background: var(--color-accent-primary);
  color: var(--color-text-on-accent);
}

.nav-icon {
  margin-right: var(--space-inline-md);
  font-size: var(--size-icon-md);
}

.nav-label {
  font-size: var(--text-base);
  font-weight: 500;
}

/* Sidebar Footer */
.sidebar-footer {
  padding: var(--space-inset-md) var(--space-inset-lg);
  border-top: 1px solid var(--color-border-subtle);
}

.user-info {
  display: flex;
  flex-direction: column;
  gap: var(--space-stack-sm);
}

.user-email {
  font-size: var(--text-label);
  color: var(--color-text-muted);
  overflow: hidden;
  text-overflow: ellipsis;
}

.logout-btn {
  padding: var(--space-inset-sm) var(--space-inset-md);
  background: var(--color-surface-glass-strong);
  color: var(--color-text-primary);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-button);
  font-size: var(--text-body);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--motion-fast-ease-out);
  backdrop-filter: blur(var(--blur-surface-glass-low));
}

.logout-btn:hover {
  background: var(--color-surface-glass);
  transform: scale(var(--motion-tap-scale));
  border-color: var(--color-accent-primary);
}

/* Main Content - Glass background, capped width, centered */
.admin-main {
  flex: 1;
  max-width: var(--size-content-max-width, 90rem);
  margin: 0 auto;
  width: 100%;
  background: var(--color-surface-primary);
  overflow-y: auto;
  padding: var(--space-inset-xl);
  min-height: 0;
}

/* Mobile: Bottom navigation (uses rem like existing code) */
@media (max-width: 48rem) {
  .admin-container {
    flex-direction: column;
  }

  .admin-sidebar {
    width: 100%;
    position: fixed;
    bottom: 0;
    height: auto;
    flex-direction: row;
    border-right: none;
    border-top: 1px solid var(--color-border-subtle);
    z-index: var(--z-bottomnav);
  }

  .sidebar-header,
  .sidebar-footer {
    display: none;
  }

  .sidebar-nav {
    display: flex;
    width: 100%;
    padding: 0;
  }

  .nav-section-title {
    display: none;
  }

  .nav-item {
    flex: 1;
    flex-direction: column;
    padding: var(--space-inset-sm);
    justify-content: center;
    margin: 0;
    border-radius: 0;
  }

  .nav-icon {
    margin-right: 0;
    margin-bottom: var(--space-stack-xs);
  }

  .nav-label {
    font-size: var(--text-label);
  }

  .admin-main {
    padding: var(--space-inset-md);
    padding-bottom: var(--space-stack-bottomnav-clearance);
  }
}
</style>
